/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: routes/SystemRoutes.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    System configuration routes (users, roles, settings, etc.)
────────────────────────────────────────────────────────────────*/

import { Route } from 'react-router-dom';
import { RoleProfilesPage, RoleProfileFormView } from '../modules/roleProfiles';
import { LogProfilesPage, LogProfileFormView as LogProfileForm } from '../modules/logProfiles';
import { UsersPage, UserFormView } from '../modules/users';
import { ApplicationKeysPage, ApiKeyFormView } from '../modules/apiKeys';
import { SystemMiscPage, SystemConfigFormView } from '../modules/system';
import { EmailSettingsPage, EmailConfigForm } from '../modules/emailSettings';
import { LoggingSettingsPage, LoggingSettingsFormView } from '../modules/loggingSettings';
import { TelephonyServersPage, ServerFormView } from '../modules/telephonyServers';
import { TelephonyDomainsPage, TelephonyDomainsFormView } from '../modules/telephonyDomains';
import { TenantsPage, TenantFormView } from '../modules/tenants';
import { AppearancePage } from '../modules/appearance';
import { LanguageManagementPage } from '../modules/languages/LanguageManagementPage';
import { UserProfilePage } from '../modules/userProfile';
import BackupRestorePage from '../modules/maintenance/backup-restore/pages/BackupRestorePage';
import SystemCleanupPage from '../modules/maintenance/system/pages/SystemCleanupPage';
import { IntegrationsMarketplace, CrmConfigurationConfig, IntegrationsList, IntegrationFormRouter } from '../modules/integrations';
import { CrmCallbackPage } from '../modules/integrations/pages/CrmCallbackPage';
import {
  SmsProvidersPage,
  SmsProviderFormView,
  SmsNumbersPage,
  SmsRoutesPage,
  SmsMessagesPage,
  SmsSettingsPage,
  SmsOptoutsPage,
} from '../modules/sms';
import {
  RoleProfilesProtectedRoute,
  LogProfilesProtectedRoute,
  UsersProtectedRoute,
  ApiKeysProtectedRoute,
  SystemMiscProtectedRoute,
  EmailSettingsProtectedRoute,
  LogSettingsProtectedRoute,
  TelephonyServersProtectedRoute,
  TelephonyDomainsProtectedRoute,
  TenantsProtectedRoute,
  AppearanceProtectedRoute,
  UserProfileProtectedRoute,
  BackupRestoreProtectedRoute,
  IntegrationsProtectedRoute,
} from '../components/ProtectedRoute';
import { ROUTES } from '../constants/routes';

export const SystemRoutes = () => (
  <>
    {/* User Management */}
    <Route path={ROUTES.ROLE_PROFILES} element={
      <RoleProfilesProtectedRoute>
        <RoleProfilesPage />
      </RoleProfilesProtectedRoute>
    } />
    <Route path={ROUTES.ROLE_PROFILE_DETAIL(':id')} element={
      <RoleProfilesProtectedRoute>
        <RoleProfileFormView />
      </RoleProfilesProtectedRoute>
    } />
    <Route path={ROUTES.LOG_PROFILES} element={
      <LogProfilesProtectedRoute>
        <LogProfilesPage />
      </LogProfilesProtectedRoute>
    } />
    <Route path={ROUTES.LOG_PROFILE_DETAIL(':id')} element={
      <LogProfilesProtectedRoute>
        <LogProfileForm />
      </LogProfilesProtectedRoute>
    } />
    <Route path={ROUTES.USERS} element={
      <UsersProtectedRoute>
        <UsersPage />
      </UsersProtectedRoute>
    } />
    <Route path={ROUTES.USER_DETAIL(':id')} element={
      <UsersProtectedRoute>
        <UserFormView />
      </UsersProtectedRoute>
    } />
    <Route path={ROUTES.USER_PROFILE} element={
      <UserProfileProtectedRoute>
        <UserProfilePage />
      </UserProfileProtectedRoute>
    } />

    {/* System Configuration */}
    <Route path="/admin/admin/app-keys" element={
      <ApiKeysProtectedRoute>
        <ApplicationKeysPage />
      </ApiKeysProtectedRoute>
    } />
    <Route path="/admin/admin/app-keys/:id" element={
      <ApiKeysProtectedRoute>
        <ApiKeyFormView />
      </ApiKeysProtectedRoute>
    } />
    <Route path="/admin/system-settings/misc" element={
      <SystemMiscProtectedRoute>
        <SystemMiscPage />
      </SystemMiscProtectedRoute>
    } />
    <Route path="/admin/system-settings/system-misc/:id" element={
      <SystemMiscProtectedRoute>
        <SystemConfigFormView />
      </SystemMiscProtectedRoute>
    } />
    <Route path="/admin/system-settings/email" element={
      <EmailSettingsProtectedRoute>
        <EmailSettingsPage />
      </EmailSettingsProtectedRoute>
    } />
    <Route path="/admin/system-settings/email/:id" element={
      <EmailSettingsProtectedRoute>
        <EmailConfigForm />
      </EmailSettingsProtectedRoute>
    } />
    <Route path="/settings/pbx/log-files" element={
      <LogSettingsProtectedRoute>
        <LoggingSettingsPage />
      </LogSettingsProtectedRoute>
    } />
    <Route path="/settings/pbx/log-files/edit" element={
      <LogSettingsProtectedRoute>
        <LoggingSettingsFormView />
      </LogSettingsProtectedRoute>
    } />
    <Route path={ROUTES.APPEARANCE} element={
      <AppearanceProtectedRoute>
        <AppearancePage />
      </AppearanceProtectedRoute>
    } />
    <Route path={ROUTES.LANGUAGES} element={
      <AppearanceProtectedRoute>
        <LanguageManagementPage />
      </AppearanceProtectedRoute>
    } />

    {/* Integrations */}
    <Route path={ROUTES.INTEGRATIONS} element={
      <IntegrationsProtectedRoute>
        <IntegrationsList />
      </IntegrationsProtectedRoute>
    } />
    <Route path={ROUTES.INTEGRATIONS_LIST} element={
      <IntegrationsProtectedRoute>
        <IntegrationsList />
      </IntegrationsProtectedRoute>
    } />
    <Route path="/settings/integrations/marketplace" element={
      <IntegrationsProtectedRoute>
        <IntegrationsMarketplace />
      </IntegrationsProtectedRoute>
    } />
    <Route path="/settings/integrations/:provider/new" element={
      <IntegrationsProtectedRoute>
        <IntegrationFormRouter />
      </IntegrationsProtectedRoute>
    } />
    <Route path="/settings/integrations/:provider/:connectionId" element={
      <IntegrationsProtectedRoute>
        <IntegrationFormRouter />
      </IntegrationsProtectedRoute>
    } />
    <Route path={ROUTES.INTEGRATION_CONFIGURATION} element={
      <IntegrationsProtectedRoute>
        <CrmConfigurationConfig />
      </IntegrationsProtectedRoute>
    } />
    <Route path="/crm/callback/:provider/:token" element={
      <CrmCallbackPage />
    } />

    <Route path={ROUTES.BACKUP_RESTORE} element={
      <BackupRestoreProtectedRoute>
        <BackupRestorePage />
      </BackupRestoreProtectedRoute>
    } />
    <Route path={ROUTES.SYSTEM_CLEANUP} element={
      <BackupRestoreProtectedRoute>
        <SystemCleanupPage />
      </BackupRestoreProtectedRoute>
    } />

    {/* Telephony Infrastructure */}
    <Route path="/admin/system-settings/telephony-servers" element={
      <TelephonyServersProtectedRoute>
        <TelephonyServersPage />
      </TelephonyServersProtectedRoute>
    } />
    <Route path="/admin/system-settings/telephony-servers/:id" element={
      <TelephonyServersProtectedRoute>
        <ServerFormView />
      </TelephonyServersProtectedRoute>
    } />
    <Route path="/admin/multi-tenant/domains" element={
      <TelephonyDomainsProtectedRoute>
        <TelephonyDomainsPage />
      </TelephonyDomainsProtectedRoute>
    } />
    <Route path="/admin/multi-tenant/domains/:id" element={
      <TelephonyDomainsProtectedRoute>
        <TelephonyDomainsFormView />
      </TelephonyDomainsProtectedRoute>
    } />
    <Route path="/admin/multi-tenant/tenants" element={
      <TenantsProtectedRoute>
        <TenantsPage />
      </TenantsProtectedRoute>
    } />
    <Route path="/admin/multi-tenant/tenants/:id" element={
      <TenantsProtectedRoute>
        <TenantFormView />
      </TenantsProtectedRoute>
    } />

    {/* SMS Messaging — uses typed ROUTES constants for consistency */}
    <Route path={ROUTES.SMS_PROVIDERS} element={<AppearanceProtectedRoute><SmsProvidersPage /></AppearanceProtectedRoute>} />
    <Route path={ROUTES.SMS_PROVIDER_DETAIL(':id')} element={<AppearanceProtectedRoute><SmsProviderFormView /></AppearanceProtectedRoute>} />
    <Route path={ROUTES.SMS_NUMBERS} element={<AppearanceProtectedRoute><SmsNumbersPage /></AppearanceProtectedRoute>} />
    <Route path={ROUTES.SMS_ROUTES} element={<AppearanceProtectedRoute><SmsRoutesPage /></AppearanceProtectedRoute>} />
    <Route path={ROUTES.SMS_MESSAGES} element={<AppearanceProtectedRoute><SmsMessagesPage /></AppearanceProtectedRoute>} />
    <Route path={ROUTES.SMS_SETTINGS} element={<AppearanceProtectedRoute><SmsSettingsPage /></AppearanceProtectedRoute>} />
    <Route path={ROUTES.SMS_OPTOUTS} element={<AppearanceProtectedRoute><SmsOptoutsPage /></AppearanceProtectedRoute>} />
  </>
);

